import { useCallback, useEffect, useRef, useState } from 'react';

interface ThreadSplitterProps {
  onWidthChange: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  className?: string;
}

/**
 * 画面の大きさに応じてスレッドビューの最小幅と最大幅を計算する
 */
const getThreadViewWidths = (): { minWidth: number; maxWidth: number } => {
  const screenWidth = window.innerWidth;
  const EDITOR_TOOL_BAR_MIN_WIDTH = 360;
  const SIDEBAR_WIDTH = 240;

  return {
    minWidth: EDITOR_TOOL_BAR_MIN_WIDTH,
    maxWidth: screenWidth - EDITOR_TOOL_BAR_MIN_WIDTH - SIDEBAR_WIDTH,
  };
};

const ThreadSplitter: React.FC<ThreadSplitterProps> = ({ onWidthChange, className = '' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [responsiveWidths, setResponsiveWidths] = useState(getThreadViewWidths());
  const splitterRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setResponsiveWidths(getThreadViewWidths());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const windowWidth = window.innerWidth;
      const newWidth = windowWidth - e.clientX;

      /*
       * マウス位置に基づいて新しい幅を計算し、最小幅と最大幅の範囲内に収める
       */
      const clampedWidth = Math.max(
        responsiveWidths.minWidth,
        Math.min(responsiveWidths.maxWidth, newWidth),
      );
      onWidthChange(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, responsiveWidths, onWidthChange]);

  return (
    <div
      ref={splitterRef}
      onMouseDown={handleMouseDown}
      className={`group relative w-1 cursor-col-resize bg-gray-200 transition-colors hover:bg-blue-500 ${
        isDragging ? 'bg-blue-500' : ''
      } ${className}`}
      role='separator'
      aria-orientation='vertical'
      aria-label='スレッドパネルのサイズを調整'
    >
      <div
        className={`absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 transition-opacity ${
          isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      />
    </div>
  );
};

export default ThreadSplitter;
